/**
 * useTenants Hook
 *
 * Manages tenant list, pagination, filtering, and CRUD operations.
 *
 * @module features/tenants/hooks/useTenants
 */

import { useState, useEffect, useCallback } from 'react';
import type { Tenant, TenantFormData } from '../types';
import {
  fetchTenants as fetchTenantsApi,
  createTenant as createTenantApi,
  updateTenant as updateTenantApi,
  archiveTenant as archiveTenantApi,
  restoreTenant as restoreTenantApi,
  purgeTenant as purgeTenantApi,
  UpdateTenantData,
} from '../api';

/**
 * Auth fetch function type.
 */
type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

/**
 * Filter state for tenant list.
 */
export interface UseTenantFilters {
  status: string;
  isolationLevel: string;
  search: string;
}

/**
 * Pagination state for tenant list.
 */
export interface UseTenantsPagination {
  page: number;
  pageSize: number;
  totalPages: number;
  total: number;
}

/**
 * Sort state for tenant list.
 */
export interface UseTenantsSort {
  by: string;
  dir: 'asc' | 'desc';
}

/**
 * Return type for useTenants hook.
 */
export interface UseTenantsResult {
  // Data
  /** List of tenants for current page/filters */
  tenants: Tenant[];
  /** Loading state for list fetch */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Clear the error message */
  clearError: () => void;

  // Pagination
  /** Current pagination state */
  pagination: UseTenantsPagination;
  /** Set current page number */
  setPage: (page: number) => void;

  // Filters
  /** Current filter values */
  filters: UseTenantFilters;
  /** Update one or more filter values */
  setFilters: (filters: Partial<UseTenantFilters>) => void;

  // Sorting
  /** Current sort state */
  sort: UseTenantsSort;
  /** Toggle sort on a column (same column toggles direction, new column defaults to asc) */
  setSort: (column: string) => void;

  // Operations
  /** Refresh the tenant list */
  refresh: () => Promise<void>;
  /** Create a new tenant */
  create: (data: TenantFormData) => Promise<Tenant>;
  /** Update an existing tenant */
  update: (id: string, data: UpdateTenantData) => Promise<Tenant>;
  /** Archive (soft-delete) a tenant */
  archive: (id: string) => Promise<void>;
  /** Restore an archived tenant */
  restore: (id: string) => Promise<Tenant>;
  /** Permanently delete a tenant (requires slug confirmation for safety) */
  purge: (id: string, confirmSlug: string) => Promise<void>;
}

const DEFAULT_PAGE_SIZE = 20;

/**
 * Hook for managing tenant list, pagination, filtering, and CRUD operations.
 *
 * @param authFetch - Authenticated fetch function from useAuth
 * @returns Tenant data, pagination, filters, and CRUD operations
 *
 * @example
 * ```tsx
 * const {
 *   tenants,
 *   loading,
 *   error,
 *   pagination,
 *   setPage,
 *   filters,
 *   setFilters,
 *   refresh,
 *   create,
 *   archive,
 * } = useTenants(authFetch);
 *
 * // Change filters
 * setFilters({ status: 'active' });
 *
 * // Create a tenant
 * try {
 *   const newTenant = await create(formData);
 *   showSuccess(`Created ${newTenant.name}`);
 * } catch (err) {
 *   showError(err.message);
 * }
 * ```
 */
export const useTenants = (authFetch: AuthFetch): UseTenantsResult => {
  // List state
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [page, setPageState] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  // Filter state
  const [filters, setFiltersState] = useState<UseTenantFilters>({
    status: 'all',
    isolationLevel: 'all',
    search: '',
  });

  // Sort state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  /**
   * Fetch tenants from the API.
   */
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTenantsApi(authFetch, {
        page,
        pageSize,
        status: filters.status,
        isolationLevel: filters.isolationLevel,
        search: filters.search,
        sortBy,
        sortDir,
      });
      setTenants(data.tenants);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch tenants';
      setError(message);
      console.error('Failed to fetch tenants:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch, page, pageSize, filters, sortBy, sortDir]);

  /**
   * Set the current page number.
   */
  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  /**
   * Update filter values. Resets page to 1.
   */
  const setFilters = useCallback((newFilters: Partial<UseTenantFilters>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
    setPageState(1); // Reset to first page when filters change
  }, []);

  /**
   * Toggle sort on a column. Same column toggles direction; new column defaults to asc.
   */
  const setSort = useCallback((column: string) => {
    if (column === sortBy) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPageState(1); // Reset to first page when sort changes
  }, [sortBy]);

  /**
   * Clear the error message.
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Create a new tenant.
   */
  const create = useCallback(async (data: TenantFormData): Promise<Tenant> => {
    const tenant = await createTenantApi(authFetch, data);
    await refresh();
    return tenant;
  }, [authFetch, refresh]);

  /**
   * Update an existing tenant.
   */
  const update = useCallback(async (id: string, data: UpdateTenantData): Promise<Tenant> => {
    const tenant = await updateTenantApi(authFetch, id, data);
    await refresh();
    return tenant;
  }, [authFetch, refresh]);

  /**
   * Archive (soft-delete) a tenant.
   */
  const archive = useCallback(async (id: string): Promise<void> => {
    await archiveTenantApi(authFetch, id);
    await refresh();
  }, [authFetch, refresh]);

  /**
   * Restore an archived tenant.
   */
  const restore = useCallback(async (id: string): Promise<Tenant> => {
    const tenant = await restoreTenantApi(authFetch, id);
    await refresh();
    return tenant;
  }, [authFetch, refresh]);

  /**
   * Permanently delete a tenant.
   * @param id - Tenant ID
   * @param confirmSlug - Tenant slug for safety confirmation
   */
  const purge = useCallback(async (id: string, confirmSlug: string): Promise<void> => {
    await purgeTenantApi(authFetch, id, confirmSlug);
    await refresh();
  }, [authFetch, refresh]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    tenants,
    loading,
    error,
    clearError,
    pagination: { page, pageSize, totalPages, total },
    setPage,
    filters,
    setFilters,
    sort: { by: sortBy, dir: sortDir },
    setSort,
    refresh,
    create,
    update,
    archive,
    restore,
    purge,
  };
};
